import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { API_CONFIG } from '../../config';
import { ICONS } from '../../constants';

interface VirtualArtistRatesPanelProps {
  canEdit: boolean;
}

interface RateData {
  id: string;
  hourlyRate: number;
}

interface RatesResponse {
  rates: Array<{
    id: string;
    virtualArtistType: string;
    specialization: string;
    hourlyRate: number;
  }>;
  bySpecialization: Record<string, Record<string, RateData>>;
  specializations: string[];
}

const VIRTUAL_TYPES = ['lead', 'senior', 'mid', 'junior'] as const;
type VirtualType = typeof VIRTUAL_TYPES[number];

const VirtualArtistRatesPanel: React.FC<VirtualArtistRatesPanelProps> = ({ canEdit }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [specializations, setSpecializations] = useState<string[]>([]);
  // Store as strings to prevent focus loss during editing
  const [rates, setRates] = useState<Record<string, Record<VirtualType, string>>>({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/rates/virtual`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch rates');
      }

      const data: RatesResponse = await response.json();
      setSpecializations(data.specializations);

      // Convert to editable format (strings)
      const ratesMap: Record<string, Record<VirtualType, string>> = {};
      for (const spec of data.specializations) {
        ratesMap[spec] = {
          lead: String(data.bySpecialization[spec]?.lead?.hourlyRate || 0),
          senior: String(data.bySpecialization[spec]?.senior?.hourlyRate || 0),
          mid: String(data.bySpecialization[spec]?.mid?.hourlyRate || 0),
          junior: String(data.bySpecialization[spec]?.junior?.hourlyRate || 0),
        };
      }
      setRates(ratesMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading rates');
    } finally {
      setLoading(false);
    }
  };

  const handleRateChange = (spec: string, type: VirtualType, value: string) => {
    // Store as string, don't parse immediately
    setRates(prev => ({
      ...prev,
      [spec]: {
        ...prev[spec],
        [type]: value,
      },
    }));
    setHasChanges(true);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Convert to API format (parse strings to numbers)
      const ratesArray: Array<{ virtualArtistType: string; specialization: string; hourlyRate: number }> = [];
      for (const [spec, typeRates] of Object.entries(rates)) {
        for (const [type, rateStr] of Object.entries(typeRates)) {
          ratesArray.push({
            virtualArtistType: type,
            specialization: spec,
            hourlyRate: parseFloat(rateStr) || 0,
          });
        }
      }

      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/rates/virtual`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rates: ratesArray }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save rates');
      }

      setSuccess(t('rates.virtual.saveSuccess') || 'Ставки сохранены');
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('rates.virtual.saveError') || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const getTypeLabel = (type: VirtualType): string => {
    return t(`rates.virtual.${type}`) || type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getTypeColor = (type: VirtualType): string => {
    switch (type) {
      case 'lead': return 'text-purple-400';
      case 'senior': return 'text-orange-400';
      case 'mid': return 'text-blue-400';
      case 'junior': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
        <span className="ml-3 text-gray-400">{t('rates.common.loading') || 'Загрузка...'}</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">
          {t('rates.virtual.title') || 'Ставки виртуальных артистов'}
        </h3>
        <p className="text-gray-400 text-sm">
          {t('rates.virtual.description') || 'Базовые ставки для Junior / Mid / Senior / Lead артистов по специализациям'}
        </p>
      </div>

      {/* Error/Success messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400 text-sm">
          {success}
        </div>
      )}

      {/* Not editable warning */}
      {!canEdit && (
        <div className="mb-4 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg text-yellow-400 text-sm">
          {t('rates.common.noPermission') || 'Недостаточно прав для редактирования'}
        </div>
      )}

      {/* Rates table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">
                {t('bidding.specialization') || 'Специализация'}
              </th>
              {VIRTUAL_TYPES.map(type => (
                <th key={type} className={`text-center py-3 px-4 font-medium text-sm ${getTypeColor(type)}`}>
                  {getTypeLabel(type)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {specializations.map((spec, index) => (
              <tr 
                key={spec} 
                className={`border-b border-gray-700/50 ${index % 2 === 0 ? 'bg-gray-800/30' : ''}`}
              >
                <td className="py-3 px-4 text-white font-medium">{spec}</td>
                {VIRTUAL_TYPES.map(type => (
                  <td key={type} className="py-2 px-2 text-center">
                    <div className="flex items-center justify-center">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={rates[spec]?.[type] ?? ''}
                        onChange={(e) => handleRateChange(spec, type, e.target.value)}
                        disabled={!canEdit}
                        className={`
                          w-24 px-2 py-1.5 text-center text-sm rounded
                          bg-gray-700 border border-gray-600 text-white
                          focus:outline-none focus:border-indigo-500
                          disabled:opacity-50 disabled:cursor-not-allowed
                        `}
                      />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Save button */}
      {canEdit && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`
              flex items-center space-x-2 px-6 py-2.5 rounded-lg font-medium transition-all
              ${hasChanges
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }
              disabled:opacity-50
            `}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{t('rates.common.loading') || 'Сохранение...'}</span>
              </>
            ) : (
              <>
                <ICONS.Edit className="w-4 h-4" />
                <span>{t('rates.common.save') || 'Сохранить'}</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default VirtualArtistRatesPanel;
